import { css } from '@emotion/react';
import React from 'react';
import { Button } from 'react-bootstrap';

import Badge from 'shared/components/nv-badge';

import {
  gray2,
  gray6,
  success,
  white,
} from 'styles/global_defaults/colors';
import { halfSpacing, largeSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

export type CredentialState = 'active' | 'draft' | 'archived';
export type CredentialBadgeInfo = {
  imageUrl: string;
  name: string;
  description: string;
  url: string;
  state?: CredentialState;
} | null;

export type CredentialBadgeCardProps = {
  badgeInfo: CredentialBadgeInfo
  viewLinkText: string;
  removeLinkText: string;
  onRemove: () => void;
  disabled?: boolean;
  noteTitle?: string;
  noteMessage?: string;
  showRemove?: boolean;
  dataQas?: {
    badgeUrl?: string;
    badgeRemove?: string;
    badgeActive?: string;
    badgeDraft?: string;
    badgeArchived?: string;
  };
};

const CredentialBadgeCard = ({
  badgeInfo,
  viewLinkText,
  removeLinkText,
  onRemove,
  disabled = false,
  noteTitle,
  noteMessage,
  showRemove = true,
  dataQas,
}: CredentialBadgeCardProps) => {
  if (!badgeInfo) {
    return null;
  }

  const {
    imageUrl,
    name,
    description,
    state,
    url,
  } = badgeInfo;
  const styles = css`
    gap: ${largeSpacing}px;
    opacity: ${disabled ? 0.5 : 1};
    .credential-badge {
      gap: ${standardSpacing}px;
    }
    .template-info {
      gap: ${halfSpacing}px;
    }

    .credential-image {
      width: 100px;
      height: 100px;
    }

    a {
      ${disabled && css`
        pointer-events: none;
        cursor: default;
      `}
    }
  `;

  const showNote = state && state !== 'active' && noteTitle && noteMessage;
  const dataQaBadgeStatus = () => {
    if (!state || !dataQas) return '';

    if (state === 'active') {
      return dataQas.badgeActive || '';
    }
    if (state === 'draft') {
      return dataQas.badgeDraft || '';
    }
    if (state === 'archived') {
      return dataQas.badgeArchived || '';
    }
    return '';
  };


  return (
    <section css={styles} className='d-flex'>
      <div className='d-flex credential-badge flex-column align-items-center flex-shrink-0'>
        {imageUrl && (
          <img
            className='credential-image rounded-circle'
            src={imageUrl}
            alt={name || 'badge image'}
          />
        )}
        {state && (
          <Badge
            color={state === 'active' ? white : gray2}
            backgroundColor={state === 'active' ? success : gray6}
            data-qa={dataQaBadgeStatus()}
          >
            {state}
          </Badge>
        )}
      </div>

      <div className='template-info d-flex flex-column justify-content-between w-100'>
        {name && (
          <p className='text-body bold'>{name}</p>
        )}

        {description && (
          <p className='text-body'>{description}</p>
        )}

        {showNote && (
          <p className='text-small text-gray-2'>
            <span className='semi-bold'>{noteTitle}</span>
            <span>{noteMessage}</span>
          </p>
        )}

        <div className='d-flex justify-content-between'>
          {url && (
            <a
              className='text-primary'
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              data-qa={dataQas?.badgeUrl}
            >
              {viewLinkText}
            </a>
          )}

          {showRemove && (
            <Button
              variant='link'
              onClick={onRemove}
              disabled={disabled}
              className='text-primary p-0'
              data-qa={dataQas?.badgeRemove}
            >
              {removeLinkText}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CredentialBadgeCard;
